import { put, call, all, takeLatest, takeEvery } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import { getAppHost } from 'helpers'
import {
  listPinnedConversations,
  replacePinnedConversations,
  type PinnedConversationTarget,
  type PinnedConversationsResponse,
} from 'services/conversations'
import actions from './actions'
import { PinnedConversationsState } from './reducers'

/**
 * LIST reads silently (loading flag only), matching the repo's other list sagas.
 * REPLACE is a write, so it notifies on both outcomes.
 */

// The root store is a plain-JS module (untyped). Read its slices through this
// boundary so the rest of the file stays typed. `apps` is untyped legacy state.
interface RootState {
  apps: { active: any; appSettings: any }
  pinnedConversations: PinnedConversationsState
}
const getRoot = (): RootState => reduxStore.getState() as unknown as RootState

// Every saga is dispatched with `{ type, payload }`; only the payload varies.
type Saga<P> = { payload?: P }

// redux-saga@1's take* overloads reject a param narrowed to `{ payload }`, so
// workers are widened at the registration boundary only.
type SagaWorker = (action: any) => any

/** A 4xx arrives as `{ error }`, not a throw — only transport failures hit catch. */
const errorMessageOf = (response: any, fallback: string): string =>
  response?.error?.message ?? fallback

/** GET /conversations/pinned — the admin-global list, already ordered. */
export function* LIST() {
  yield put({ type: actions.SET_STATE, payload: { loading: true, error: null } })

  const { active: activeApp, appSettings } = getRoot().apps

  try {
    const success: PinnedConversationsResponse = yield call(
      listPinnedConversations,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && Array.isArray(success.data)) {
      yield put({
        type: actions.SET_STATE,
        payload: { saved: success.data, loading: false, error: null },
      })
      return
    }

    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
        error: errorMessageOf(success, 'Failed to load pinned conversations'),
      },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { loading: false, error: 'Failed to load pinned conversations' },
    })
  }
}

interface ReplacePayload {
  /** The desired final list, in order. `[]` clears it. */
  targets: PinnedConversationTarget[]
}

/**
 * The new baseline is the server's response, not the draft we sent — it de-dupes
 * and drops unresolvable rows. On failure the baseline is left untouched.
 */
export function* REPLACE({ payload }: Saga<ReplacePayload>) {
  yield put({ type: actions.SET_STATE, payload: { saving: true, error: null } })

  const { active: activeApp, appSettings } = getRoot().apps
  const targets = payload?.targets ?? []

  try {
    const success: PinnedConversationsResponse = yield call(
      replacePinnedConversations,
      activeApp,
      getAppHost(appSettings),
      targets,
    )

    if (success && Array.isArray(success.data)) {
      yield put({
        type: actions.SET_STATE,
        payload: { saved: success.data, saving: false, error: null },
      })
      window.triggerNotification?.('Pinned conversations updated', 'success')
      return
    }

    const message = errorMessageOf(success, 'Failed to update pinned conversations')
    yield put({ type: actions.SET_STATE, payload: { saving: false, error: message } })
    window.triggerNotification?.(message, 'error')
  } catch (error) {
    const message = 'Failed to update pinned conversations'
    yield put({ type: actions.SET_STATE, payload: { saving: false, error: message } })
    window.triggerNotification?.(message, 'error')
  }
}

export default function* pinnedConversationsSaga() {
  yield all([
    takeLatest(actions.LIST, LIST as SagaWorker),
    takeEvery(actions.REPLACE, REPLACE as SagaWorker),
  ])
}
